<script setup lang="ts">
import { onMounted, ref, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute } from 'vue-router';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import { GebaeudeCollection } from '@/models/Gebaeude';
import { FilterMatchMode } from '@primevue/core/api';
import type { DataTableFilterEvent } from 'primevue/datatable';
import KpMandantKantonSelect from '@/components/KpMandantKantonSelect.vue';
import KpJahrSelect from '@/components/KpJahrSelect.vue';
import KpTextField from '@/components/KpTextField.vue';

const route = useRoute();

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'id' },
    { direction: 'desc', property: 'jahr' },
    { direction: 'asc', property: 'kanton_id' },
]);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const { collection, serverTotal } = useCollection(new GebaeudeCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

type Filter = {
    property: string;
    value: any;
    id: string;
    operator?: string;
};
const filters = ref({
    kanton_id: { value: null, matchMode: FilterMatchMode.EQUALS },
    jahr: { value: null, matchMode: FilterMatchMode.EQUALS },
    egid: { value: null, matchMode: FilterMatchMode.CONTAINS },
    nutzungsart: { value: null, matchMode: FilterMatchMode.CONTAINS },
    angrenzende_gebaeude: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name_des_gebaeudes: { value: null, matchMode: FilterMatchMode.CONTAINS },
    strassenname: { value: null, matchMode: FilterMatchMode.CONTAINS },
    hausnummer: { value: null, matchMode: FilterMatchMode.CONTAINS },
    plz: { value: null, matchMode: FilterMatchMode.CONTAINS },
    ort: { value: null, matchMode: FilterMatchMode.CONTAINS },
    bfs_gemeindenummer: { value: null, matchMode: FilterMatchMode.CONTAINS },
    gebaeudestatus: { value: null, matchMode: FilterMatchMode.CONTAINS },
    gebaeudekategorie: { value: null, matchMode: FilterMatchMode.CONTAINS },
    gebaeudeklasse: { value: null, matchMode: FilterMatchMode.CONTAINS },
    baujahr: { value: null, matchMode: FilterMatchMode.CONTAINS },
    bauperiode_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    gebaeudeflaeche: { value: null, matchMode: FilterMatchMode.CONTAINS },
    gebaeudevolumen: { value: null, matchMode: FilterMatchMode.CONTAINS },
    anzahl_geschosse: { value: null, matchMode: FilterMatchMode.CONTAINS },
    anzahl_wohnungen_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    anzahl_zimmer_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    wohnungsflaeche_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    energiebezugsflaeche: { value: null, matchMode: FilterMatchMode.CONTAINS },
    energiebezugsflaeche_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    waermeerzeuger_heizung_1: { value: null, matchMode: FilterMatchMode.CONTAINS },
    waermeerzeuger_heizung_2: { value: null, matchMode: FilterMatchMode.CONTAINS },
    energie_waermequelle_heizung_1: { value: null, matchMode: FilterMatchMode.CONTAINS },
    energie_waermequelle_heizung_2: { value: null, matchMode: FilterMatchMode.CONTAINS },
    datum_aktualisierung_heizung_1: { value: null, matchMode: FilterMatchMode.CONTAINS },
    datum_aktualisierung_heizung_2: { value: null, matchMode: FilterMatchMode.CONTAINS },
    waermeerzeuger_warmwasser_1: { value: null, matchMode: FilterMatchMode.CONTAINS },
    waermeerzeuger_warmwasser_2: { value: null, matchMode: FilterMatchMode.CONTAINS },
    energie_waermequelle_warmwasser_1: { value: null, matchMode: FilterMatchMode.CONTAINS },
    energie_waermequelle_warmwasser_2: { value: null, matchMode: FilterMatchMode.CONTAINS },
    datum_aktualisierung_warmwasser_1: { value: null, matchMode: FilterMatchMode.CONTAINS },
    datum_aktualisierung_warmwasser_2: { value: null, matchMode: FilterMatchMode.CONTAINS },
    e_koordinate: { value: null, matchMode: FilterMatchMode.CONTAINS },
    n_koordinate: { value: null, matchMode: FilterMatchMode.CONTAINS },
    waermeerzeuger_heizung_1_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    waermeerzeuger_heizung_2_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    energie_waermequelle_heizung_1_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    energie_waermequelle_heizung_2_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    waermeerzeuger_warmwasser_1_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    waermeerzeuger_warmwasser_2_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    energie_waermequelle_warmwasser_1_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    energie_waermequelle_warmwasser_2_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    anzahl_angrenzende_beheizte_gebaeude_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    anteil_heizung_1_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    anteil_heizung_2_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    nutzungsgrad_heizung_1_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    nutzungsgrad_heizung_2_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    standard_nutzenergie_raumwaerme_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    standard_waermebedarf_heizung_1_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
    standard_waermebedarf_heizung_2_calc: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const filter: Ref<Filter[]> = ref([]);
function onFilterChange(filterEvent: DataTableFilterEvent) {
    const prop = 'value';
    filter.value = [];
    for (const key in filterEvent.filters) {
        // @ts-expect-error it's save, do it!
        const value = filterEvent.filters[key].hasOwnProperty(prop) ? filterEvent.filters[key][prop] : null;
        filter.value.push({
            property: key,
            id: key,
            operator: '=',
            value,
        });
    }
    collection.setQueryParams({ filter: filter.value });
    collection.query();
}

onMounted(() => {
    if (collection.loading.value !== true) {
        collection.query();
    }
});
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :showRecordEditBtn="false"
            :showRecordDeleteBtn="false"
            :showReloadBtn="false"
            :showCsvDownloadBtn="false"
            :showAddBtn="false"
            :showCopyBtn="false"
            :collection="collection"
            v-model:table-sort="tableSort"
            v-model:filters="filters"
            :title="$t('gebaeude')"
            filterDisplay="row"
            scrollable
            scroll-height="flex"
            paginator
            v-model:page="page"
            v-model:pageSize="pageSize"
            :total-records="serverTotal"
            @filter="onFilterChange"
        >
            <Column field="id" sortable :header="$t('id')" style="width: 5rem"></Column>
            <Column
                field="kanton"
                sortable
                :header="$t('kanton')"
                style="width: 5rem"
                :showFilterMenu="false"
                sortField="kanton_id"
                filterField="kanton_id"
            >
                <template #filter="{ filterModel, filterCallback }">
                    <KpMandantKantonSelect
                        v-model="filterModel.value"
                        fluid
                        @change="filterCallback()"
                    ></KpMandantKantonSelect>
                </template>
            </Column>
            <Column field="jahr" sortable :header="$t('jahr')" style="width: 5rem" :showFilterMenu="false">
                <template #filter="{ filterModel, filterCallback }">
                    <KpJahrSelect
                        fluid
                        :start-year="2015"
                        v-model="filterModel.value"
                        @change="filterCallback()"
                    ></KpJahrSelect> </template
            ></Column>
            <Column field="egid" sortable :header="$t('egid')" style="width: 5rem">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="nutzungsart" sortable :header="$t('nutzungsart')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="angrenzende_gebaeude" sortable :header="$t('angrenzende_gebaeude')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="name_des_gebaeudes" sortable :header="$t('name_des_gebaeudes')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="strassenname" sortable :header="$t('strassenname')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="hausnummer" sortable :header="$t('hausnummer')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="plz" sortable :header="$t('plz')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="ort" sortable :header="$t('ort')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="bfs_gemeindenummer" sortable :header="$t('bfs_gemeindenummer')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="gebaeudestatus" sortable :header="$t('gebaeudestatus')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="gebaeudekategorie" sortable :header="$t('gebaeudekategorie')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="gebaeudeklasse" sortable :header="$t('gebaeudeklasse')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="baujahr" sortable :header="$t('baujahr')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="bauperiode_calc" sortable :header="$t('bauperiode_calc')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="gebaeudeflaeche" sortable :header="$t('gebaeudeflaeche')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="gebaeudevolumen" sortable :header="$t('gebaeudevolumen')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="anzahl_geschosse" sortable :header="$t('anzahl_geschosse')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="anzahl_wohnungen_calc" sortable :header="$t('anzahl_wohnungen_calc')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="anzahl_zimmer_calc" sortable :header="$t('anzahl_zimmer_calc')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="wohnungsflaeche_calc" sortable :header="$t('wohnungsflaeche_calc')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="energiebezugsflaeche" sortable :header="$t('energiebezugsflaeche')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="energiebezugsflaeche_calc" sortable :header="$t('energiebezugsflaeche_calc')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="waermeerzeuger_heizung_1" sortable :header="$t('waermeerzeuger_heizung_1')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="waermeerzeuger_heizung_2" sortable :header="$t('waermeerzeuger_heizung_2')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="energie_waermequelle_heizung_1" sortable :header="$t('energie_waermequelle_heizung_1')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="energie_waermequelle_heizung_2" sortable :header="$t('energie_waermequelle_heizung_2')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="datum_aktualisierung_heizung_1" sortable :header="$t('datum_aktualisierung_heizung_1')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="datum_aktualisierung_heizung_2" sortable :header="$t('datum_aktualisierung_heizung_2')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="waermeerzeuger_warmwasser_1" sortable :header="$t('waermeerzeuger_warmwasser_1')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="waermeerzeuger_warmwasser_2" sortable :header="$t('waermeerzeuger_warmwasser_2')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column
                field="energie_waermequelle_warmwasser_1"
                sortable
                :header="$t('energie_waermequelle_warmwasser_1')"
            >
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column
                field="energie_waermequelle_warmwasser_2"
                sortable
                :header="$t('energie_waermequelle_warmwasser_2')"
            >
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column
                field="datum_aktualisierung_warmwasser_1"
                sortable
                :header="$t('datum_aktualisierung_warmwasser_1')"
            >
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column
                field="datum_aktualisierung_warmwasser_2"
                sortable
                :header="$t('datum_aktualisierung_warmwasser_2')"
            >
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="e_koordinate" sortable :header="$t('e_koordinate')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="n_koordinate" sortable :header="$t('n_koordinate')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="waermeerzeuger_heizung_1_calc" sortable :header="$t('waermeerzeuger_heizung_1_calc')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="waermeerzeuger_heizung_2_calc" sortable :header="$t('waermeerzeuger_heizung_2_calc')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column
                field="energie_waermequelle_heizung_1_calc"
                sortable
                :header="$t('energie_waermequelle_heizung_1_calc')"
            >
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column
                field="energie_waermequelle_heizung_2_calc"
                sortable
                :header="$t('energie_waermequelle_heizung_2_calc')"
            >
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="waermeerzeuger_warmwasser_1_calc" sortable :header="$t('waermeerzeuger_warmwasser_1_calc')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="waermeerzeuger_warmwasser_2_calc" sortable :header="$t('waermeerzeuger_warmwasser_2_calc')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column
                field="energie_waermequelle_warmwasser_1_calc"
                sortable
                :header="$t('energie_waermequelle_warmwasser_1_calc')"
            >
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column
                field="energie_waermequelle_warmwasser_2_calc"
                sortable
                :header="$t('energie_waermequelle_warmwasser_2_calc')"
            >
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column
                field="anzahl_angrenzende_beheizte_gebaeude_calc"
                sortable
                :header="$t('anzahl_angrenzende_beheizte_gebaeude_calc')"
            >
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="anteil_heizung_1_calc" sortable :header="$t('anteil_heizung_1_calc')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="anteil_heizung_2_calc" sortable :header="$t('anteil_heizung_2_calc')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="nutzungsgrad_heizung_1_calc" sortable :header="$t('nutzungsgrad_heizung_1_calc')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="nutzungsgrad_heizung_2_calc" sortable :header="$t('nutzungsgrad_heizung_2_calc')">
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column
                field="standard_nutzenergie_raumwaerme_calc"
                sortable
                :header="$t('standard_nutzenergie_raumwaerme_calc')"
            >
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column
                field="standard_waermebedarf_heizung_1_calc"
                sortable
                :header="$t('standard_waermebedarf_heizung_1_calc')"
            >
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column
                field="standard_waermebedarf_heizung_2_calc"
                sortable
                :header="$t('standard_waermebedarf_heizung_2_calc')"
            >
                <template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="anteil_warmwasser_1_calc" sortable :header="$t('anteil_warmwasser_1_calc')"></Column>
            <Column field="anteil_warmwasser_2_calc" sortable :header="$t('anteil_warmwasser_2_calc')"></Column>
            <Column
                field="nutzungsgrad_warmwasser_1_calc"
                sortable
                :header="$t('nutzungsgrad_warmwasser_1_calc')"
            ></Column>
            <Column
                field="nutzungsgrad_warmwasser_2_calc"
                sortable
                :header="$t('nutzungsgrad_warmwasser_2_calc')"
            ></Column>
            <Column
                field="standard_nutzenergie_warmwasser_calc"
                sortable
                :header="$t('standard_nutzenergie_warmwasser_calc')"
            ></Column>
            <Column
                field="standard_waermebedarf_warmwasser_1_calc"
                sortable
                :header="$t('standard_waermebedarf_warmwasser_1_calc')"
            ></Column>
            <Column
                field="standard_waermebedarf_warmwasser_2_calc"
                sortable
                :header="$t('standard_waermebedarf_warmwasser_2_calc')"
            ></Column>
            <Column field="waermebedarf_heizung_calc" sortable :header="$t('waermebedarf_heizung_calc')"></Column>
            <Column field="waermebedarf_warmwasser_calc" sortable :header="$t('waermebedarf_warmwasser_calc')"></Column>
            <Column field="waermebedarf_total_calc" sortable :header="$t('waermebedarf_total_calc')"></Column>
            <Column field="e_gebaeudekoordinate" sortable :header="$t('e_gebaeudekoordinate')"></Column>
            <Column field="n_gebaeudekoordinate" sortable :header="$t('n_gebaeudekoordinate')"></Column>
        </KpBaseCollectionGrid>
    </div>
</template>
